import { ASSISTANT_VOICE_PREFERENCES } from '../constants/config';

let cachedVoice = null;
let hasVoiceListener = false;

export function isSpeechSupported() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Picks the most kid-like, natural-sounding English voice from what the browser offers.
 * Voices are tried in the order of ASSISTANT_VOICE_PREFERENCES; the first match wins.
 */
export function pickAssistantVoice(voices) {
  const englishVoices = voices.filter((voice) => voice.lang?.toLowerCase().startsWith('en'));
  for (const preference of ASSISTANT_VOICE_PREFERENCES) {
    const match = englishVoices.find((voice) => preference.namePattern.test(voice.name));
    if (match) return { voice: match, pitch: preference.pitch, rate: preference.rate };
  }
  const fallback = englishVoices[0] ?? null;
  return { voice: fallback, pitch: 1.4, rate: 1 };
}

function getAssistantVoice() {
  if (cachedVoice?.voice) return cachedVoice;
  const voices = window.speechSynthesis.getVoices();
  if (voices.length === 0) return { voice: null, pitch: 1.4, rate: 1 };
  cachedVoice = pickAssistantVoice(voices);
  return cachedVoice;
}

/**
 * Browsers load their voice list in the background; warm it up early so the
 * first "Read to me" already uses Fizzy's voice.
 */
export function preloadAssistantVoice() {
  if (!isSpeechSupported() || hasVoiceListener) return;
  hasVoiceListener = true;
  getAssistantVoice();
  window.speechSynthesis.addEventListener?.('voiceschanged', () => {
    cachedVoice = null;
    getAssistantVoice();
  });
}

/**
 * Reads text aloud in Fizzy's voice. onEnd runs when the speech finishes, is stopped, or fails.
 */
export function speakText(text, { onEnd } = {}) {
  if (!isSpeechSupported() || !text) {
    onEnd?.();
    return;
  }
  window.speechSynthesis.cancel();
  const utterance = new window.SpeechSynthesisUtterance(text);
  const { voice, pitch, rate } = getAssistantVoice();
  if (voice) {
    utterance.voice = voice;
    utterance.lang = voice.lang;
  }
  utterance.pitch = pitch;
  utterance.rate = rate;
  if (onEnd) {
    utterance.addEventListener('end', onEnd, { once: true });
    utterance.addEventListener('error', onEnd, { once: true });
  }
  window.speechSynthesis.speak(utterance);
}

export function stopSpeaking() {
  if (isSpeechSupported()) window.speechSynthesis.cancel();
}
